import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import type { UserInfoResponse } from '@logto/vue'
import axios from 'axios'
import i18n from '@/i18n'
import { userApi } from '@/manage/api/user'
import type {
  LinkedInAccountType,
  UpdateUserBusinessProfilePayload,
  UserAsset,
  UserBusinessProfile,
  UserProfileIdentity,
} from '@/manage/types/user'
import { logger } from '@/utils/logger'
import { appStorage } from '@/utils/storage/app'
import { logtoStorage } from '@/utils/storage/logto'
import { envConfig } from '@/shared/config/env'
import { usePostHog } from '@/shared/composables/usePostHog'
import {
  syncPostHogIdentity as applyPostHogIdentity,
  syncPostHogLogout,
} from '@/shared/analytics/posthogIdentity'
import { logtoBridge } from '@/shared/auth/logtoBridge'
import { LOGTO_SCOPES } from '@/manage/constants/access'
import { isAuthFailure, isNetworkFailure } from '@/manage/stores/userErrorSignals'
import { parseTokenScopes, getTokenExpiryAtMs } from '@/manage/stores/userTokenScopes'
import { createUserSync } from '@/manage/stores/userSync'

export const useUserStore = defineStore('user', () => {
  const ACCESS_TOKEN_REFRESH_LEEWAY_MS = 2 * 60 * 1000
  const VISIBILITY_REFRESH_LEEWAY_MS = 60 * 1000
  const EXTENSION_HELLO_RESPONSE_COOLDOWN_MS = 5000
  const AUTH_BROADCAST_CHANNEL = 'easyhire_auth_channel'
  const AUTH_BROADCAST_KEY = 'easyhire_auth_broadcast'
  const REFRESH_LOCK_KEY = 'easyhire_auth_refresh_lock'
  const REFRESH_LOCK_TTL_MS = 15_000
  const REFRESH_LOCK_POLL_INTERVAL_MS = 200
  const isExtensionDebugEnabled = import.meta.env.DEV && appStorage.isExtensionDebugEnabled()
  const tabId = `tab_${Math.random().toString(36).slice(2)}_${Date.now().toString(36)}`

  type LocalAuthSnapshot = {
    token: string | null
    user: UserInfoResponse | null
    adminMode: boolean
  }

  type FreshTokenOptions = {
    allowStaleOnNetworkError?: boolean
    force?: boolean
    reason?: string
  }

  type EnsureSessionOptions = {
    force?: boolean
  }

  type NullableBusinessProfile = {
    [K in keyof UserBusinessProfile]?: UserBusinessProfile[K] | null
  }

  type UserIdentityAvatarMap = Record<
    string,
    {
      details?: {
        avatar?: string | null
        rawData?: {
          picture?: string | null
        }
      }
    }
  >

  type AvatarCandidate = {
    id: string
    source: string
    url: string
  }

  type RefreshLockState = {
    expiresAt: number
    ownerTabId: string
  }

  const { posthog, hasPostHogKey } = usePostHog()

  const parseStoredUser = (raw = appStorage.getUserProfileRaw()) => {
    if (!raw) return null
    try {
      return JSON.parse(raw) as UserInfoResponse
    } catch (error) {
      logger.error('Failed to parse user info from localStorage', error)
      posthog.capture('storage_error', {
        operation: 'parse_user',
        storageType: 'localStorage',
        error: String(error),
      })
      return null
    }
  }

  const token = ref<string | null>(appStorage.getUserToken())
  const user = ref<UserInfoResponse | null>(parseStoredUser())
  const preferredAvatarUrl = ref<string | null>(null)
  const adminMode = ref(appStorage.isAdminModeEnabled())
  const sessionReady = ref(false)
  const isSigningOut = ref(false)
  const sessionLastEnsuredAt = ref<number | null>(null)
  let postHogIdentitySyncQueue = Promise.resolve()
  let lastPostHogIdentitySignature: string | undefined
  let postHogIdentityRetryTimer: ReturnType<typeof setTimeout> | null = null
  const postHogIdentityRetryDelays = [1_000, 5_000] as const

  const getPostHogIdentitySignature = (userInfo: UserInfoResponse | null) =>
    userInfo
      ? JSON.stringify([
          userInfo.sub,
          userInfo.email,
          (userInfo as { primaryEmail?: string | null }).primaryEmail,
          userInfo.username,
          userInfo.name,
        ])
      : 'anonymous'

  const clearPostHogIdentityRetry = () => {
    if (!postHogIdentityRetryTimer) return
    clearTimeout(postHogIdentityRetryTimer)
    postHogIdentityRetryTimer = null
  }

  const schedulePostHogIdentityRetry = (
    signature: string,
    retryAttempt: number,
    retry: () => void,
  ) => {
    const delay = postHogIdentityRetryDelays[retryAttempt]
    if (delay === undefined) return
    clearPostHogIdentityRetry()
    postHogIdentityRetryTimer = setTimeout(() => {
      postHogIdentityRetryTimer = null
      if (getPostHogIdentitySignature(user.value) === signature) retry()
    }, delay)
  }

  const enqueuePostHogIdentitySync = (operation: () => Promise<void>) => {
    postHogIdentitySyncQueue = postHogIdentitySyncQueue
      .then(operation)
      .catch((error) => logger.warn('Failed to synchronize PostHog identity', error))
  }
  let ensureSessionPromise: Promise<boolean> | null = null
  let scheduledRefreshTimer: number | null = null

  const syncPostHogExtensionConnected = (eventType?: string) => {
    if (!hasPostHogKey) return
    posthog.capture('extension_connected', {
      source: 'post_message',
      event_type: eventType || '',
    })
  }

  const syncFromStorage = (reason = 'storage_sync') => {
    applySnapshot(
      {
        token: appStorage.getUserToken(),
        user: parseStoredUser(),
        adminMode: appStorage.isAdminModeEnabled(),
      },
      false,
      reason,
    )
  }

  const {
    extensionLastSeenAt,
    sendTokenToExtension,
    markExtensionSeen,
    emitAuthEvent,
    attachSyncListeners,
    close: closeUserSync,
  } = createUserSync({
    authBroadcastChannel: AUTH_BROADCAST_CHANNEL,
    authBroadcastKey: AUTH_BROADCAST_KEY,
    refreshLockKey: REFRESH_LOCK_KEY,
    extensionHelloResponseCooldownMs: EXTENSION_HELLO_RESPONSE_COOLDOWN_MS,
    isExtensionDebugEnabled,
    tabId,
    getToken: () => token.value,
    getUser: () => user.value,
    onStorageSync: (reason) => syncFromStorage(reason),
    onBroadcastSync: (reason) => syncFromStorage(reason),
    onExtensionFirstSeen: syncPostHogExtensionConnected,
  })

  const syncPostHogIdentity = (userInfo: UserInfoResponse | null, retryAttempt = 0) => {
    if (!hasPostHogKey) return
    if (!userInfo && token.value) {
      const pendingSignature = 'authenticated_user_pending'
      if (lastPostHogIdentitySignature === pendingSignature) return
      if (retryAttempt === 0) clearPostHogIdentityRetry()
      lastPostHogIdentitySignature = pendingSignature
      posthog.set_identity_capture_pending()
      return
    }
    const signature = getPostHogIdentitySignature(userInfo)
    if (lastPostHogIdentitySignature === signature) return
    if (retryAttempt === 0) clearPostHogIdentityRetry()
    lastPostHogIdentitySignature = signature
    // Close the gate synchronously so events fired immediately after setUser()
    // wait for the new identity instead of being attributed to the old one.
    const generation = posthog.set_identity_capture_pending()
    enqueuePostHogIdentitySync(async () => {
      try {
        await applyPostHogIdentity(posthog, userInfo, undefined, generation)
      } catch (error) {
        if (posthog.is_identity_capture_current(generation)) {
          lastPostHogIdentitySignature = undefined
          posthog.set_identity_capture_pending()
          schedulePostHogIdentityRetry(signature, retryAttempt, () => {
            syncPostHogIdentity(user.value, retryAttempt + 1)
          })
        }
        throw error
      }
    })
  }

  const syncPostHogLogoutState = (retryAttempt = 0) => {
    if (!hasPostHogKey) return
    if (retryAttempt === 0) clearPostHogIdentityRetry()
    lastPostHogIdentitySignature = 'anonymous'
    const generation = posthog.set_identity_capture_pending()
    enqueuePostHogIdentitySync(async () => {
      try {
        await syncPostHogLogout(posthog, generation)
      } catch (error) {
        if (posthog.is_identity_capture_current(generation)) {
          lastPostHogIdentitySignature = undefined
          posthog.set_identity_capture_pending()
          schedulePostHogIdentityRetry('anonymous', retryAttempt, () => {
            syncPostHogLogoutState(retryAttempt + 1)
          })
        }
        throw error
      }
    })
  }

  const applySnapshot = (
    snapshot: LocalAuthSnapshot,
    shouldBroadcast = false,
    reason = 'snapshot',
  ) => {
    if (isSigningOut.value && (snapshot.token || snapshot.user)) return
    const previousToken = token.value
    const previousUser = user.value
    token.value = snapshot.token
    user.value = snapshot.user
    adminMode.value = snapshot.adminMode

    if (snapshot.token) {
      appStorage.setUserToken(snapshot.token)
    } else {
      appStorage.clearUserToken()
      asset.value = null
      assetUpdatedAt.value = null
    }

    if (snapshot.user) {
      appStorage.setUserProfileRaw(JSON.stringify(snapshot.user))
    } else {
      appStorage.clearUserProfile()
    }

    appStorage.setAdminMode(snapshot.adminMode)
    sendTokenToExtension(snapshot.token, snapshot.user)
    if (!snapshot.token && !snapshot.user && (previousToken || previousUser)) {
      syncPostHogLogoutState()
    } else {
      syncPostHogIdentity(snapshot.user)
    }

    if (shouldBroadcast && previousToken !== snapshot.token) {
      emitAuthEvent(snapshot.token ? 'snapshot' : 'logout', reason)
    }
  }

  const setToken = (newToken: string, shouldBroadcast = true, reason = 'token_updated') => {
    if (isSigningOut.value) return
    token.value = newToken
    appStorage.setUserToken(newToken)
    sendTokenToExtension(newToken, user.value)
    if (!user.value) syncPostHogIdentity(null)
    if (shouldBroadcast) {
      emitAuthEvent('snapshot', reason)
    }
  }

  const setUser = (userInfo: UserInfoResponse, shouldBroadcast = true, reason = 'user_updated') => {
    if (isSigningOut.value) return
    user.value = userInfo
    appStorage.setUserProfileRaw(JSON.stringify(userInfo))
    sendTokenToExtension(token.value, userInfo)
    syncPostHogIdentity(userInfo)
    if (shouldBroadcast) {
      emitAuthEvent('snapshot', reason)
    }
  }

  const clearUser = (shouldBroadcast = true, reason = 'logout') => {
    user.value = null
    token.value = null
    appStorage.clearUserProfile()
    appStorage.clearUserToken()
    logtoStorage.clearPersistedSession()
    appStorage.setAdminMode(false)
    sendTokenToExtension(null, null)
    asset.value = null
    assetUpdatedAt.value = null
    businessProfile.value = null
    businessProfileUpdatedAt.value = null
    adminMode.value = false
    syncPostHogLogoutState()
    sessionReady.value = true
    if (scheduledRefreshTimer) {
      clearTimeout(scheduledRefreshTimer)
      scheduledRefreshTimer = null
    }
    if (shouldBroadcast) {
      emitAuthEvent('logout', reason)
    }
  }

  const beginSignOut = () => {
    // Mark the transition before reactive cleanup can trigger more requests.
    isSigningOut.value = true
    clearUser()
  }

  const isAuthenticated = computed(() => !!token.value)

  const normalizeTargetResource = (resource: string | null | undefined) =>
    resource === undefined ? envConfig.logtoResourceApi : resource || undefined

  const readRefreshLock = (): RefreshLockState | null => {
    if (typeof window === 'undefined') return null
    try {
      const raw = window.localStorage.getItem(REFRESH_LOCK_KEY)
      if (!raw) return null
      const parsed = JSON.parse(raw) as RefreshLockState
      if (!parsed?.ownerTabId || !parsed?.expiresAt) return null
      return parsed
    } catch (error) {
      console.error('[User Store] 读取刷新锁失败:', error)
      posthog.capture('storage_error', {
        operation: 'read_refresh_lock',
        storageType: 'localStorage',
        error: String(error),
      })
      return null
    }
  }

  const tryAcquireRefreshLock = () => {
    if (typeof window === 'undefined') return true
    const now = Date.now()
    const current = readRefreshLock()
    if (current && current.ownerTabId !== tabId && current.expiresAt > now) {
      return false
    }

    const nextLock: RefreshLockState = {
      ownerTabId: tabId,
      expiresAt: now + REFRESH_LOCK_TTL_MS,
    }

    try {
      window.localStorage.setItem(REFRESH_LOCK_KEY, JSON.stringify(nextLock))
      const confirmedLock = readRefreshLock()
      return confirmedLock?.ownerTabId === tabId
    } catch (error) {
      console.error('[User Store] 获取刷新锁失败:', error)
      posthog.capture('storage_error', {
        operation: 'acquire_refresh_lock',
        storageType: 'localStorage',
        error: String(error),
      })
      return false
    }
  }

  const releaseRefreshLock = () => {
    if (typeof window === 'undefined') return
    const current = readRefreshLock()
    if (!current || current.ownerTabId !== tabId) return
    try {
      window.localStorage.removeItem(REFRESH_LOCK_KEY)
    } catch (error) {
      console.error('[User Store] 释放刷新锁失败:', error)
      posthog.capture('storage_error', {
        operation: 'release_refresh_lock',
        storageType: 'localStorage',
        error: String(error),
      })
      // Ignore lock cleanup failures.
    }
  }

  const waitForPeerRefresh = async (previousToken: string | null) => {
    const start = Date.now()
    while (Date.now() - start < REFRESH_LOCK_TTL_MS + 1000) {
      const latestToken = appStorage.getUserToken()
      if (latestToken && latestToken !== previousToken) {
        setToken(latestToken, false, 'peer_refresh')
        return latestToken
      }

      const currentLock = readRefreshLock()
      if (!currentLock || currentLock.expiresAt <= Date.now()) {
        break
      }

      await new Promise((resolve) => window.setTimeout(resolve, REFRESH_LOCK_POLL_INTERVAL_MS))
    }

    const latestToken = appStorage.getUserToken()
    if (latestToken && latestToken !== previousToken) {
      setToken(latestToken, false, 'peer_refresh_final')
      return latestToken
    }

    return null
  }

  // Sync on init
  sendTokenToExtension(token.value, user.value)
  syncPostHogIdentity(user.value)

  const handleWindowBeforeUnload = () => {
    closeUserSync()
    if (typeof window !== 'undefined') {
      window.removeEventListener('beforeunload', handleWindowBeforeUnload)
    }
  }

  attachSyncListeners()
  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', handleWindowBeforeUnload)
  }

  const isTokenExpiringSoon = (
    rawToken: string | null,
    leewayMs = ACCESS_TOKEN_REFRESH_LEEWAY_MS,
  ) => {
    const expiresAtMs = getTokenExpiryAtMs(rawToken)
    if (!expiresAtMs) return false
    return expiresAtMs <= Date.now() + leewayMs
  }

  const grantedScopes = computed(() => parseTokenScopes(token.value))
  const hasScope = (scope: string) => grantedScopes.value.has(scope)
  const canContactFetchTag = computed(() => hasScope(LOGTO_SCOPES.contactFetchTag))
  const canContactFetchPage = computed(
    () => hasScope(LOGTO_SCOPES.contactFetchPage) || canContactFetchTag.value,
  )
  const canAdminRead = computed(() => hasScope(LOGTO_SCOPES.adminRead))
  const canReportExport = computed(() => hasScope(LOGTO_SCOPES.reportExport))
  const canEmailManage = computed(() => hasScope(LOGTO_SCOPES.emailManage))
  const canEmailSend = computed(() => hasScope(LOGTO_SCOPES.emailSend))
  const canInmail = computed(() => hasScope(LOGTO_SCOPES.inmail))
  const canAgent = computed(() => hasScope(LOGTO_SCOPES.agent))
  const canWhatsApp = computed(() => hasScope(LOGTO_SCOPES.whatsapp))

  const scheduleTokenRefresh = () => {
    if (typeof window === 'undefined') return
    if (scheduledRefreshTimer) {
      window.clearTimeout(scheduledRefreshTimer)
      scheduledRefreshTimer = null
    }

    const expiresAtMs = getTokenExpiryAtMs(token.value)
    if (!token.value || !expiresAtMs) return

    const delay = Math.max(expiresAtMs - Date.now() - ACCESS_TOKEN_REFRESH_LEEWAY_MS, 15_000)
    scheduledRefreshTimer = window.setTimeout(() => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
        scheduleTokenRefresh()
        return
      }
      void getFreshToken(undefined, {
        allowStaleOnNetworkError: true,
        force: true,
        reason: 'scheduled_refresh',
      })
    }, delay)
  }

  const setAdminMode = (
    enabled: boolean,
    shouldBroadcast = true,
    reason = 'admin_mode_updated',
  ) => {
    adminMode.value = enabled
    appStorage.setAdminMode(enabled)
    if (shouldBroadcast) {
      emitAuthEvent('snapshot', reason)
    }
  }

  const toggleAdminMode = () => {
    setAdminMode(!adminMode.value)
  }

  const isAdminModeActive = computed(() => canAdminRead.value && adminMode.value)

  const getFreshToken = async (
    resource: string | null | undefined = undefined,
    options: FreshTokenOptions = {},
  ): Promise<string | null> => {
    if (isSigningOut.value) return null
    const {
      allowStaleOnNetworkError = resource === undefined,
      force = false,
      reason = 'default',
    } = options

    if (resource === undefined && token.value && !force && !isTokenExpiringSoon(token.value)) {
      return token.value
    }

    if (!logtoBridge.isReady()) {
      return resource === undefined ? token.value : null
    }

    const targetResource = normalizeTargetResource(resource)
    const previousToken = token.value
    const needsCrossTabLock =
      resource === undefined && (force || !token.value || isTokenExpiringSoon(token.value))

    if (needsCrossTabLock && !tryAcquireRefreshLock()) {
      const peerToken = await waitForPeerRefresh(previousToken)
      if (isSigningOut.value) return null
      if (peerToken && !isTokenExpiringSoon(peerToken)) {
        return peerToken
      }
      if (!tryAcquireRefreshLock()) {
        return peerToken
      }
    }

    try {
      const { token: nextToken, error: logtoError } =
        await logtoBridge.getAccessTokenResult(targetResource)

      if (isSigningOut.value) return null

      if (!nextToken) {
        if (logtoError) {
          if (isAuthFailure(logtoError)) {
            logger.warn(`Token refresh failed due to auth state (${reason})`, logtoError)
            return null
          }
          if (
            isNetworkFailure(logtoError) &&
            allowStaleOnNetworkError &&
            resource === undefined &&
            token.value
          ) {
            logger.warn(
              `Token refresh hit network issue, reusing cached token (${reason})`,
              logtoError,
            )
            return token.value
          }
          logger.warn(`Token refresh failed without token result (${reason})`, logtoError)
        }
        return resource === undefined && allowStaleOnNetworkError ? token.value : null
      }

      if (resource === undefined && nextToken !== token.value) {
        setToken(nextToken, true, force ? 'token_refreshed_forced' : 'token_refreshed')
      }

      return nextToken
    } finally {
      if (needsCrossTabLock) {
        releaseRefreshLock()
      }
    }
  }

  const ensureSession = async ({ force = false }: EnsureSessionOptions = {}) => {
    if (!force && sessionReady.value) {
      return isAuthenticated.value
    }
    if (ensureSessionPromise) {
      return ensureSessionPromise
    }

    ensureSessionPromise = (async () => {
      if (!logtoStorage.hasPersistedSession()) {
        clearUser(false, 'ensure_session_missing_persisted_state')
        sessionReady.value = true
        sessionLastEnsuredAt.value = Date.now()
        return false
      }

      const restoredToken = await getFreshToken(undefined, {
        allowStaleOnNetworkError: false,
        force,
        reason: force ? 'ensure_session_force' : 'ensure_session',
      })

      if (!restoredToken) {
        clearUser(false, 'ensure_session_token_missing')
        sessionReady.value = true
        sessionLastEnsuredAt.value = Date.now()
        return false
      }

      if (!user.value || force) {
        const { user: nextUser, error: logtoError } = await logtoBridge.fetchUserInfoResult()

        if (nextUser) {
          setUser(nextUser, true, force ? 'user_restored_force' : 'user_restored')
        } else if (logtoError && isAuthFailure(logtoError)) {
          clearUser(false, 'ensure_session_user_failed')
          sessionReady.value = true
          sessionLastEnsuredAt.value = Date.now()
          return false
        }
      }

      sessionReady.value = true
      sessionLastEnsuredAt.value = Date.now()
      return true
    })().finally(() => {
      ensureSessionPromise = null
      scheduleTokenRefresh()
    })

    return ensureSessionPromise
  }

  const forceRefreshDefaultToken = async () => {
    logtoStorage.clearAccessTokenCache()
    return getFreshToken(undefined, {
      allowStaleOnNetworkError: false,
      force: true,
      reason: 'force_refresh',
    })
  }

  const refreshUserInfoLatest = async () => {
    if (!logtoBridge.isReady()) return
    const { user: latestUser, error } = await logtoBridge.fetchUserInfoLatestResult()
    if (latestUser) {
      setUser(latestUser, true, 'user_refreshed_latest')
      void fetchAsset(true)
    } else if (error) {
      logger.warn('Failed to refresh user info with latest=true', error)
    }
  }

  const refreshSessionOnVisibility = async () => {
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return
    if (!logtoStorage.hasPersistedSession()) {
      clearUser(false, 'visibility_no_persisted_session')
      return
    }
    await ensureSession({
      force: !token.value || isTokenExpiringSoon(token.value, VISIBILITY_REFRESH_LEEWAY_MS),
    })
  }

  watch(
    () => canAdminRead.value,
    (allowed) => {
      if (!allowed && adminMode.value) {
        setAdminMode(false, true, 'admin_mode_revoked')
      }
    },
    { immediate: true },
  )

  watch(
    () => token.value,
    () => {
      scheduleTokenRefresh()
    },
    { immediate: true },
  )

  // 优先级: name -> username -> email
  const displayName = computed(() => {
    if (!user.value) return i18n.global.t('profile.notLoggedIn')
    return (
      user.value.name ||
      user.value.username ||
      (user.value as { primaryEmail?: string | null }).primaryEmail ||
      user.value.email ||
      i18n.global.t('profile.unknownUser')
    )
  })

  const normalizeAvatarUrl = (value: unknown) => String(value || '').trim()

  const getAvatarPreferenceUserId = (userInfo: UserInfoResponse | null) =>
    String(
      userInfo?.sub ||
        userInfo?.email ||
        (userInfo as { primaryEmail?: string | null } | null)?.primaryEmail ||
        userInfo?.username ||
        (userInfo as { id?: string | null } | null)?.id ||
        '',
    ).trim()

  const avatarPreferenceUserId = computed(() => getAvatarPreferenceUserId(user.value))

  const avatarCandidates = computed<AvatarCandidate[]>(() => {
    const userInfo = user.value
    const candidates: AvatarCandidate[] = []
    const seen = new Set<string>()

    const pushCandidate = (source: string, value: unknown) => {
      const url = normalizeAvatarUrl(value)
      if (!url || seen.has(url)) return
      seen.add(url)
      candidates.push({
        id: `${source}:${candidates.length}`,
        source,
        url,
      })
    }

    pushCandidate('profile', userInfo?.picture)

    if (!userInfo?.identities || typeof userInfo.identities !== 'object') return candidates

    const identities = userInfo.identities as UserIdentityAvatarMap
    const preferredProviders = ['linkedin', 'google', 'facebook', 'feishu'] as const

    for (const provider of preferredProviders) {
      const details = identities[provider]?.details
      pushCandidate(provider, details?.rawData?.picture)
      pushCandidate(provider, details?.avatar)
    }

    for (const [provider, identity] of Object.entries(identities)) {
      if (preferredProviders.includes(provider as (typeof preferredProviders)[number])) continue
      pushCandidate(provider, identity?.details?.rawData?.picture)
      pushCandidate(provider, identity?.details?.avatar)
    }

    return candidates
  })

  // 头像: picture -> identities.*.details.rawData.picture -> identities.*.details.avatar -> 默认头像
  const avatarUrl = computed(() => {
    const selectedAvatar = preferredAvatarUrl.value
    if (selectedAvatar && avatarCandidates.value.some((item) => item.url === selectedAvatar)) {
      return selectedAvatar
    }
    if (avatarCandidates.value[0]?.url) return avatarCandidates.value[0].url
    // 使用 ui-avatars 生成默认头像，基于用户名
    const name =
      displayName.value !== i18n.global.t('profile.notLoggedIn') ? displayName.value : 'User'
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=random`
  })

  const setPreferredAvatarUrl = (url: string | null) => {
    const userId = avatarPreferenceUserId.value
    if (!userId) return

    const normalizedUrl = normalizeAvatarUrl(url)
    if (normalizedUrl && avatarCandidates.value.some((item) => item.url === normalizedUrl)) {
      preferredAvatarUrl.value = normalizedUrl
      appStorage.setAvatarPreference(userId, normalizedUrl)
      return
    }

    preferredAvatarUrl.value = null
    appStorage.clearAvatarPreference(userId)
  }

  watch(
    avatarPreferenceUserId,
    (userId) => {
      preferredAvatarUrl.value = userId ? appStorage.getAvatarPreference(userId) : null
    },
    { immediate: true },
  )

  const asset = ref<UserAsset | null>(null)
  const assetLoading = ref(false)
  const assetUpdatedAt = ref<number | null>(null)
  let assetPromise: Promise<UserAsset | null> | null = null
  let assetRequestSeq = 0
  const businessProfile = ref<UserBusinessProfile | null>(null)
  const businessProfileLoading = ref(false)
  const businessProfileUpdatedAt = ref<number | null>(null)
  let businessProfilePromise: Promise<UserBusinessProfile | null> | null = null

  const normalizeAsset = (payload: unknown): UserAsset | null => {
    if (!payload) return null
    if (typeof payload === 'object' && payload && 'data' in payload) {
      const data = (payload as { data?: UserAsset }).data
      return data ?? null
    }
    return payload as UserAsset
  }

  const isCanceledRequestError = (error: unknown) => {
    if (axios.isCancel(error)) return true
    if (!error || typeof error !== 'object') return false
    const requestError = error as { code?: unknown; name?: unknown; message?: unknown }
    return (
      requestError.code === 'ERR_CANCELED' ||
      requestError.name === 'CanceledError' ||
      requestError.message === 'canceled'
    )
  }

  const fetchAsset = async (force = false) => {
    if (!force && asset.value) return asset.value
    if (assetPromise) return assetPromise
    assetLoading.value = true
    const requestSeq = ++assetRequestSeq
    const promise = (async () => {
      try {
        const response = await userApi.getUserAsset()
        const normalized = normalizeAsset(response)
        if (normalized && requestSeq === assetRequestSeq) {
          asset.value = normalized
          assetUpdatedAt.value = Date.now()
        }
        return normalized ?? asset.value
      } catch (error) {
        if (isCanceledRequestError(error)) {
          return asset.value
        }
        console.error('[User Store] 获取用户资产失败:', error)
        posthog.capture('api_request_error', {
          module: 'user',
          operation: 'fetch_asset',
          error: String(error),
        })
        return asset.value
      } finally {
        if (requestSeq === assetRequestSeq) {
          assetLoading.value = false
          assetPromise = null
        }
      }
    })()
    assetPromise = promise
    return promise
  }

  const setAssetPoints = (points: number) => {
    if (!Number.isFinite(points)) return
    const normalizedPoints = Math.max(0, Math.floor(points))
    if (asset.value) {
      asset.value = {
        ...asset.value,
        contact_points: normalizedPoints,
      }
    } else {
      asset.value = {
        contact_points: normalizedPoints,
        ai_match_points: 0,
        job_count: 0,
        vip_expire_at: null,
        is_vip: false,
      }
    }
    assetUpdatedAt.value = Date.now()
  }

  const normalizeBusinessProfile = (
    payload: NullableBusinessProfile | null,
  ): UserBusinessProfile | null => {
    if (!payload) return null

    const identity: UserProfileIdentity = payload.identity === 'headhunter' ? 'headhunter' : 'hr'
    const linkedinAccountType = String(payload.linkedin_account_type || '')
      .trim()
      .toLowerCase() as LinkedInAccountType

    return {
      hiring_company: String(payload.hiring_company || '').trim(),
      identity,
      name: String(payload.name || '').trim(),
      title: String(payload.title || '').trim(),
      linkedin_account_type: linkedinAccountType,
      contact: String(payload.contact || '').trim(),
    }
  }

  const fetchBusinessProfile = async (force = false) => {
    if (!force && businessProfile.value) return businessProfile.value
    if (businessProfilePromise) return businessProfilePromise
    businessProfileLoading.value = true
    businessProfilePromise = (async () => {
      try {
        const response = await userApi.getUserProfile()
        const normalized = normalizeBusinessProfile(response)
        businessProfile.value = normalized
        businessProfileUpdatedAt.value = Date.now()
        return businessProfile.value
      } catch (error) {
        console.error('[User Store] 获取用户业务资料失败:', error)
        posthog.capture('api_request_error', {
          module: 'user',
          operation: 'fetch_business_profile',
          error: String(error),
        })
        throw error
      } finally {
        businessProfileLoading.value = false
        businessProfilePromise = null
      }
    })()
    return businessProfilePromise
  }

  const updateBusinessProfile = async (payload: UpdateUserBusinessProfilePayload) => {
    businessProfileLoading.value = true
    try {
      const response = await userApi.updateUserProfile(payload)
      const normalized = normalizeBusinessProfile({
        ...payload,
        ...response,
      })
      businessProfile.value = normalized
      businessProfileUpdatedAt.value = Date.now()
      return businessProfile.value
    } finally {
      businessProfileLoading.value = false
    }
  }

  const assetPoints = computed(() => asset.value?.contact_points ?? 0)
  const assetAiMatchPoints = computed(() => asset.value?.ai_match_points ?? 0)
  const assetJobCount = computed(() => asset.value?.job_count ?? 0)
  const assetIsVip = computed(() => !!asset.value?.is_vip)
  const assetVipExpireAt = computed(() => asset.value?.vip_expire_at ?? null)
  const canRecruitingAi = computed(() => canAgent.value)

  return {
    user,
    token,
    setUser,
    setToken,
    getFreshToken,
    ensureSession,
    sessionReady,
    sessionLastEnsuredAt,
    clearUser,
    beginSignOut,
    isSigningOut,
    isAuthenticated,
    displayName,
    avatarUrl,
    avatarCandidates,
    setPreferredAvatarUrl,
    asset,
    assetLoading,
    assetUpdatedAt,
    fetchAsset,
    businessProfile,
    businessProfileLoading,
    businessProfileUpdatedAt,
    fetchBusinessProfile,
    updateBusinessProfile,
    setAssetPoints,
    assetPoints,
    assetAiMatchPoints,
    assetJobCount,
    assetIsVip,
    assetVipExpireAt,
    grantedScopes,
    hasScope,
    canContactFetchPage,
    canContactFetchTag,
    canAdminRead,
    canReportExport,
    canEmailManage,
    canEmailSend,
    canInmail,
    canAgent,
    canWhatsApp,
    canRecruitingAi,
    adminMode,
    setAdminMode,
    toggleAdminMode,
    isAdminModeActive,
    forceRefreshDefaultToken,
    refreshUserInfoLatest,
    refreshSessionOnVisibility,
    syncToExtension: (source = 'unknown') => {
      if (hasPostHogKey) {
        posthog.capture('extension_sync_requested', {
          source,
        })
      }
      sendTokenToExtension(token.value, user.value)
    },
    extensionLastSeenAt,
    markExtensionSeen,
  }
})
